import { c as _c } from "react/compiler-runtime";
import { BREAKPOINTS } from '@hookdeck/theme';
import { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import { stringifyQuery } from '../../../../../utils';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import Chip from '../../../../common/base/Chip';
import Icon from '../../../../common/base/Icon';
import { Div } from '../../../../common/helpers/StyledUtils';
import BaseMetricChart from '../../../../common/metrics/MetricChart';
import ChartInteractionGroup from '../../../../common/Chart/ChartInteractionGroup';
import { default_data_formatter } from '../../../../common/metrics/metric-definitions';
import { ChartDataSet } from '../../../../common/Chart/Chart';
import useViewportBreakpoint from '../../../../hooks/useViewportBreakpoint';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataDescription, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardContext';
import MetricChart from '../../Metrics/MetricChart';
import MetricDatePicker, { getDateRangeQueryParam, useDateRange } from '../../Metrics/MetricDatePicker';
import { useRefreshKey } from '../../Metrics/RefreshKeyContext';
import { ChartData, MetricConfigs, useMetric } from '../../Metrics/useMetric';
const StyledOverviewMetricsGrid = styled(Div)`
  @media (max-width: 1400px) {
    grid-template-columns: minmax(0, 1fr) !important;
  }
`;
const overview_columns_for_breakpoints: Record<keyof typeof BREAKPOINTS, number> = {
  mobile: 1,
  tablet: 1,
  laptop: 2,
  desktop: 2
};
const event_status_configs = [{
  value: 'SUCCESSFUL',
  theme_color: 'success',
  label: 'Successful'
}, {
  value: 'FAILED',
  theme_color: 'danger',
  label: 'Failed'
}, {
  value: 'QUEUED',
  theme_color: 'primary',
  label: 'Pending'
}, {
  value: 'HOLD',
  color: 'warning',
  label: 'Paused'
}, {
  value: 'SCHEDULED',
  color: 'warning',
  label: 'Delayed'
}, {
  value: 'CANCELLED',
  color: 'neutral_histogram',
  label: 'Cancelled'
}] as const;
const toDataset = (data: ChartData | null | undefined, config: {
  key: string;
  label: string;
  sort_order?: number;
  metric_type: ChartDataSet['metric_type'];
  theme_color?: ChartDataSet['theme_color'];
  hex_color?: string;
  getDataLabel?: ChartDataSet['getDataLabel'];
}): ChartDataSet | null => {
  if (!data) {
    return null;
  }
  return {
    ...data,
    ...config
  };
};
const ConnectionOverviewMetrics: React.FC<{
  connection_id: string;
}> = t0 => {
  const $ = _c(53);
  const {
    connection_id
  } = t0;
  const breakpoint = useViewportBreakpoint();
  const theme = useTheme();
  const {
    refresh_key,
    updateRefreshKey
  } = useRefreshKey();
  const [date_range, onDateRangeChanged] = useDateRange("last_hour");
  let t1;
  if ($[0] !== connection_id) {
    t1 = {
      filters: {
        webhook_id: [connection_id]
      }
    };
    $[0] = connection_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== date_range || $[3] !== t1) {
    t2 = {
      date_range,
      options: t1
    };
    $[2] = date_range;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const metric_configs = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["status"];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== connection_id) {
    t4 = {
      dimensions: t3,
      filters: {
        webhook_id: [connection_id]
      }
    };
    $[6] = connection_id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== date_range || $[9] !== t4) {
    t5 = {
      date_range,
      options: t4
    };
    $[8] = date_range;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const events_status_metric_configs = t5;
  const {
    data: events_status
  } = useMetric("chart:bar", "events_count", refresh_key, events_status_metric_configs);
  const events_status_loading = events_status === undefined;
  let t6;
  bb0: {
    if (events_status_loading) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[11] !== events_status || $[12] !== theme) {
      t7 = event_status_configs.map((config, index) => toDataset(events_status?.[config.value], {
        key: config.value,
        label: config.label,
        sort_order: index,
        metric_type: "count",
        theme_color: "theme_color" in config ? config.theme_color : undefined,
        hex_color: "color" in config ? theme.colors.surface.base[config.color] : undefined,
        getDataLabel: default_data_formatter.count
      })).filter(_temp);
      $[11] = events_status;
      $[12] = theme;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = t7;
  }
  const events_status_datasets = t6;
  let t7;
  if ($[14] !== date_range || $[15] !== onDateRangeChanged) {
    t7 = <MetricDatePicker date_range={date_range} onDateRangeChanged={onDateRangeChanged} />;
    $[14] = date_range;
    $[15] = onDateRangeChanged;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== updateRefreshKey) {
    t8 = <Button outline={true} icon="reload" onClick={() => updateRefreshKey()}>Refresh</Button>;
    $[17] = updateRefreshKey;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <ViewSplitPaneContentHeader title="Metrics overview" actions={<>{t7}{t8}</>} />;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== onDateRangeChanged) {
    t10 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[22] = onDateRangeChanged;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      direction: "column",
      gap: 6
    };
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== connection_id || $[26] !== date_range) {
    t12 = stringifyQuery({
      date: getDateRangeQueryParam(date_range),
      webhook_id: [connection_id]
    });
    $[25] = connection_id;
    $[26] = date_range;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = `/events?${t12}`;
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon icon="link" pointer={true} right={2} />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13) {
    t15 = <Button ghost={true} small={true} to={t13}>Explore{t14}</Button>;
    $[29] = t13;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== events_status_datasets || $[32] !== events_status_loading || $[33] !== t15) {
    t16 = <BaseMetricChart title="Events" chart_type="bar" datasets={events_status_datasets} datasets_type="stacked" force_legend={true} loading={events_status_loading} action={t15} />;
    $[31] = events_status_datasets;
    $[32] = events_status_loading;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = overview_columns_for_breakpoints[breakpoint];
  let t18;
  if ($[35] !== t17) {
    t18 = {
      columns: t17,
      gap: 6
    };
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  let t20;
  if ($[37] !== metric_configs) {
    t19 = <MetricChart metric="events_error_rate" configs={metric_configs} />;
    t20 = <MetricChart metric="events_average_attempts" configs={metric_configs} />;
    $[37] = metric_configs;
    $[38] = t19;
    $[39] = t20;
  } else {
    t19 = $[38];
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t18 || $[41] !== t19 || $[42] !== t20) {
    t21 = <StyledOverviewMetricsGrid grid={t18}>{t19}{t20}</StyledOverviewMetricsGrid>;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t16 || $[45] !== t21) {
    t22 = <Div flex={t11}>{t16}{t21}</Div>;
    $[44] = t16;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t10 || $[48] !== t22) {
    t23 = <ChartInteractionGroup onTimeRangeSelected={t10}>{t22}</ChartInteractionGroup>;
    $[47] = t10;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t23 || $[51] !== t9) {
    t24 = <>{t9}{t23}</>;
    $[50] = t23;
    $[51] = t9;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
};
export const ConnectionOverview = t0 => {
  const $ = _c(41);
  const {
    connection
  } = t0;
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const t1 = `/connections/${connection.id}/settings`;
  let t2;
  if ($[0] !== t1) {
    t2 = <ViewSplitPaneSidebarHeader title="Connection details" action={<Button outline={true} icon="edit" to={t1}>Edit</Button>} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== connection.disabled_at || $[3] !== connection.paused_at) {
    t3 = <ViewMetadataItem label="Status">{connection.paused_at ? <Badge warning={true} subtle={true} icon="pause_circle">Paused</Badge> : connection.disabled_at ? <Badge neutral={true} icon="disable">Disabled</Badge> : <Badge success={true} subtle={true} icon="bolt">Active</Badge>}</ViewMetadataItem>;
    $[2] = connection.disabled_at;
    $[3] = connection.paused_at;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = connection.name || connection.full_name;
  let t5;
  if ($[5] !== t4) {
    t5 = <ViewMetadataItem label="Name"><ViewMetadataCopyable value={t4} wrap={true} /></ViewMetadataItem>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== connection.description) {
    t6 = connection.description && <ViewMetadataItem label="Description"><ViewMetadataDescription>{connection.description}</ViewMetadataDescription></ViewMetadataItem>;
    $[7] = connection.description;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `/sources/${connection.source.id}`;
  let t8;
  if ($[9] !== connection.source.name || $[10] !== t7) {
    t8 = <ViewMetadataItem label="Source"><Chip outline={true} mono={true} label={connection.source.name} to={t7} /></ViewMetadataItem>;
    $[9] = connection.source.name;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = `/destinations/${connection.destination.id}`;
  let t10;
  if ($[12] !== connection.destination.name || $[13] !== t9) {
    t10 = <ViewMetadataItem label="Destination"><Chip outline={true} mono={true} label={connection.destination.name} to={t9} /></ViewMetadataItem>;
    $[12] = connection.destination.name;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t3 || $[17] !== t5 || $[18] !== t6 || $[19] !== t8) {
    t11 = <ViewSplitPaneSidebarSection>{t3}{t5}{t6}{t8}{t10}</ViewSplitPaneSidebarSection>;
    $[15] = t10;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== connection.id) {
    t12 = <ViewMetadataItem label="Connection ID"><ViewMetadataCopyable value={connection.id} /></ViewMetadataItem>;
    $[21] = connection.id;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== connection.created_at) {
    t13 = <ViewMetadataItem label="Created at"><ViewMetadataDate date={connection.created_at} /></ViewMetadataItem>;
    $[23] = connection.created_at;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== connection.updated_at) {
    t14 = <ViewMetadataItem label="Last updated"><ViewMetadataDate date={connection.updated_at} /></ViewMetadataItem>;
    $[25] = connection.updated_at;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t13 || $[29] !== t14) {
    t15 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}>{t12}{t13}{t14}</ViewSplitPaneSidebarSection>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t11 || $[32] !== t15 || $[33] !== t2) {
    t16 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t2}{t11}{t15}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[31] = t11;
    $[32] = t15;
    $[33] = t2;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== connection.id) {
    t17 = <StyledViewSplitPaneContent><ConnectionOverviewMetrics connection_id={connection.id} /></StyledViewSplitPaneContent>;
    $[35] = connection.id;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== is_degraded_banner_active || $[38] !== t16 || $[39] !== t17) {
    t18 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t16}{t17}</StyledViewSplitPaneLayout>;
    $[37] = is_degraded_banner_active;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};
function _temp(dataset) {
  return !!dataset;
}